import {getRootStore} from '../../../app.bootstrap';
import {getDashboardStores} from '../../../states/dashboard/dashboard.stores';
import {IUser} from '../../../models/User';
import {ITsInterfaceHelperService} from '../../../services/ts-interface-helper/ts-interface-helper.service';
import {WizardState} from '../../../services/ts-wizard-helper/ts-wizard-helper.service';
import {action, computed} from 'mobx';
import {IAccountData} from '@techsee/techsee-common/lib/constants/account.constants';
import PatternsConstant from '@techsee/techsee-common/lib/constants/utils.patterns';

export interface CurrentUser extends IUser {
    showWizard?: boolean;
}

export interface IEntryController {
    readonly showWizard: boolean;
    readonly dir: string;
    platform: string | null;
    readonly wizardState: WizardState;
    readonly cameraModeOnly: boolean;
    readonly chromeDetection: boolean;
    readonly isIE11: boolean;
    readonly tsInterfaceHelper: ITsInterfaceHelperService;
    readonly mobilePhonePattern: RegExp | string;
    readonly smsSendingMessage: string | null;
    readonly isObserveMode: boolean;

    showInstructionsFor(platform?: string | null): void;
    isVisible(platform: string): boolean;
    inSelectScreen(): boolean;
    back(): void;
    isMobile(): boolean;
    endMeeting(): void;
}

export class EntryController implements IEntryController {
    readonly dir: string;
    readonly cameraModeOnly: boolean;
    readonly chromeDetection: boolean;
    readonly isIE11: boolean;
    readonly tsInterfaceHelper: ITsInterfaceHelperService;
    readonly mobilePhonePattern: RegExp | string;
    readonly wizardState: WizardState;

    private dashboardStores = getDashboardStores();
    static $inject = ['accountData', 'currentUser', 'wizardState'];

    constructor(accountData: IAccountData, currentUser: CurrentUser, wizardState: WizardState) {
        const rootStore = getRootStore();

        this.dir = rootStore.tsLocaleHelper.isRTL() ? 'rtl' : 'ltr';
        this.cameraModeOnly = accountData.settings?.cameraModeOnly ?? false;
        this.chromeDetection = accountData.protectedSettings?.chromeDetection ?? false;
        this.isIE11 = rootStore.environmentService.isIE11();
        this.tsInterfaceHelper = rootStore.tsInterfaceHelperService;
        this.mobilePhonePattern = PatternsConstant.mobilePhone;
        this.wizardState = wizardState;

        this.applyWizardState(accountData, currentUser);
    }

    @computed
    get platform(): string | null {
        return this.wizardState.platform;
    }

    @computed
    get showWizard(): boolean {
        return this.dashboardStores.dashboardState.showWizard;
    }

    @computed
    get smsSendingMessage(): string | null {
        return this.dashboardStores.dashboardState.messageAlert?.label || null;
    }

    @computed
    get isObserveMode(): boolean {
        return this.dashboardStores.dashboardState.isObserve;
    }

    isMobile(): boolean {
        return getRootStore().environmentService.isMobile();
    }

    private applyWizardState(accountData: IAccountData, currentUser: CurrentUser): void {
        if (this.wizardState.showWizard && !(accountData.settings?.showWizard && currentUser.showWizard)) {
            this.wizardState.showWizard = false;
        }

        this.dashboardStores.dashboardState.showWizard = this.wizardState.showWizard;
    }

    @action
    showInstructionsFor = (platform?: string | null): void => {
        this.wizardState.platform = platform ?? null;
    };

    isVisible(platform: string): boolean {
        return this.platform === platform;
    }

    inSelectScreen(): boolean {
        return !this.platform;
    }

    @action
    back = (): void => {
        this.showInstructionsFor(null);
    };

    endMeeting = (): void => {
        const rootStore = getRootStore();

        if (rootStore.chatApi?.endMeeting) {
            rootStore.chatApi.endMeeting(false, 'dashboard');
        }
    };
}
